// Hidden recovery overlay for the player route.
//
// Activated by Ctrl+Alt+Shift+D or by triple-tap in the corner of the
// viewport. Designed for on-site techs who need to:
//   * verify which screen the device is bound to
//   * see the last heartbeat status
//   * clear the stored token and bounce back to /provision
//
// We deliberately hide the overlay from end-users. There is no visual
// affordance during normal playback, and dismissal restores the original
// player surface without remounting the engine.

import { useCallback, useEffect, useRef, useState } from 'react';
import { clearStoredToken, readStoredToken } from '@/lib/player-token-storage';
import type { PlayerStatus } from '@/lib/player-engine';

interface Props {
  screenId: string;
  status: PlayerStatus;
}

export function PlayerRecoveryOverlay({ screenId, status }: Props) {
  const [open, setOpen] = useState(false);
  const tapRef = useRef<{ count: number; first: number }>({ count: 0, first: 0 });

  // Key combo: Ctrl+Alt+Shift+D. Avoids any single-key combo Chromium
  // intercepts in kiosk mode and keeps the surface unreachable for casual
  // bystanders.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.ctrlKey && e.altKey && e.shiftKey && (e.key === 'D' || e.key === 'd')) {
        e.preventDefault();
        setOpen((v) => !v);
      } else if (open && e.key === 'Escape') {
        setOpen(false);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  // Triple-tap top-right corner: same gesture works on touchscreens or
  // remote controls where keyboard input is awkward. The hot zone is
  // intentionally tiny (32x32 dp) and invisible.
  const onCornerPointer = useCallback(() => {
    const now = Date.now();
    if (now - tapRef.current.first > 1500) {
      tapRef.current = { count: 1, first: now };
      return;
    }
    tapRef.current.count += 1;
    if (tapRef.current.count >= 3) {
      tapRef.current = { count: 0, first: 0 };
      setOpen(true);
    }
  }, []);

  return (
    <>
      <div
        aria-hidden
        onPointerDown={onCornerPointer}
        className="pointer-events-auto fixed right-0 top-0 z-[50] h-8 w-8 cursor-default"
        data-testid="player-recovery-hot-zone"
      />
      {open && <RecoveryPanel screenId={screenId} status={status} onClose={() => setOpen(false)} />}
    </>
  );
}

function RecoveryPanel({
  screenId,
  status,
  onClose,
}: {
  screenId: string;
  status: PlayerStatus;
  onClose: () => void;
}) {
  const [tokenHint, setTokenHint] = useState<string>('');
  useEffect(() => {
    const t = readStoredToken(screenId);
    if (t) setTokenHint(`${t.slice(0, 6)}…${t.slice(-4)}`);
  }, [screenId]);

  function handleResetToken() {
    if (!confirm('Clear the saved player token? The device will go back to the missing-token screen.'))
      return;
    clearStoredToken(screenId);
    window.location.replace(`/play/${screenId}`);
  }

  function handleRepair() {
    if (
      !confirm(
        'Re-pair this device? The current token will be removed and the device will return to /provision to display a new pairing code.',
      )
    )
      return;
    clearStoredToken(screenId);
    try {
      sessionStorage.removeItem('signage:pairing-session');
      sessionStorage.removeItem('signage:provisioned-redirect');
    } catch {
      /* ignore */
    }
    window.location.replace('/provision');
  }

  function handleClearCaches() {
    if (!confirm('Wipe Service Worker + Cache API + IndexedDB? Player will re-cache on next sync.'))
      return;
    void wipeAllCaches().finally(() => window.location.reload());
  }

  return (
    <div
      role="dialog"
      aria-modal
      className="fixed inset-0 z-[60] flex flex-col bg-black/95 text-white/90"
      onClick={(e) => {
        if (e.currentTarget === e.target) onClose();
      }}
    >
      <div className="mx-auto my-8 w-full max-w-2xl rounded-md border border-white/20 bg-black p-6 shadow-2xl">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-white/40">Recovery</div>
            <h2 className="mt-1 text-xl font-semibold">Active Sign Player diagnostics</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-white/20 px-2 py-0.5 text-xs hover:bg-white/10"
          >
            Close
          </button>
        </div>

        <dl className="mt-6 grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
          <Row label="Screen ID" value={screenId} mono />
          <Row label="Token hint" value={tokenHint || '—'} mono />
          <Row label="Hostname" value={safeHostname()} mono />
          <Row label="User agent" value={navigator.userAgent} mono />
          <Row
            label="Cache status"
            value={status.meta?.cacheStatus ?? '—'}
            tone={
              status.meta?.cacheStatus === 'ready'
                ? 'ok'
                : status.meta?.cacheStatus === 'error'
                ? 'err'
                : 'warn'
            }
          />
          <Row
            label="Heartbeat source"
            value={status.heartbeatSource ?? '—'}
            tone={status.heartbeatSource === 'edge' ? 'ok' : 'warn'}
          />
          <Row label="Active revision" value={status.activeRevisionId ?? '—'} mono />
          <Row label="Bundle version" value={status.bundleVersion ?? '—'} mono />
          <Row label="Schedule" value={status.source ?? 'default'} />
          <Row
            label="Last error"
            value={status.error ?? '—'}
            tone={status.error ? 'err' : undefined}
          />
        </dl>

        <div className="mt-6 flex flex-wrap gap-2 border-t border-white/10 pt-4">
          <Action onClick={handleResetToken} label="Reset stored token" />
          <Action onClick={handleClearCaches} label="Wipe caches" />
          <Action onClick={handleRepair} label="Re-pair this device" tone="danger" />
        </div>

        <div className="mt-4 text-[10px] tracking-[0.2em] text-white/30">
          Ctrl+Alt+Shift+D toggles this panel · triple-tap top-right corner also opens it
        </div>
      </div>
    </div>
  );
}

function Row({
  label,
  value,
  mono,
  tone,
}: {
  label: string;
  value: string | number;
  mono?: boolean;
  tone?: 'ok' | 'warn' | 'err';
}) {
  const color =
    tone === 'ok'
      ? 'text-emerald-300'
      : tone === 'warn'
      ? 'text-amber-300'
      : tone === 'err'
      ? 'text-rose-300'
      : 'text-white/80';
  return (
    <>
      <dt className="text-xs uppercase tracking-wider text-white/40">{label}</dt>
      <dd className={`${mono ? 'font-mono text-xs' : ''} ${color} truncate`} title={String(value)}>
        {value}
      </dd>
    </>
  );
}

function Action({
  onClick,
  label,
  tone,
}: {
  onClick: () => void;
  label: string;
  tone?: 'danger';
}) {
  const cls =
    tone === 'danger'
      ? 'border-rose-400/50 text-rose-200 hover:bg-rose-500/15'
      : 'border-white/20 text-white/80 hover:bg-white/10';
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded border px-3 py-1.5 text-xs ${cls}`}
    >
      {label}
    </button>
  );
}

function safeHostname(): string {
  try {
    return window.location.hostname;
  } catch {
    return 'unknown';
  }
}

async function wipeAllCaches(): Promise<void> {
  try {
    if ('caches' in self) {
      const names = await caches.keys();
      await Promise.all(names.map((n) => caches.delete(n)));
    }
  } catch {
    /* ignore */
  }
  try {
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
  } catch {
    /* ignore */
  }
  try {
    if ('indexedDB' in self && 'databases' in indexedDB) {
      const dbs = await (indexedDB as unknown as { databases: () => Promise<{ name?: string }[]> }).databases();
      await Promise.all(
        (dbs ?? []).map(
          (db) =>
            new Promise<void>((resolve) => {
              if (!db?.name) return resolve();
              const r = indexedDB.deleteDatabase(db.name);
              r.onsuccess = r.onerror = r.onblocked = () => resolve();
            }),
        ),
      );
    }
  } catch {
    /* ignore */
  }
}
